// src/utils/saveFile.js
//
// Save a text file (CSV etc.) in a way that works everywhere:
//
//  - Web browser: the normal <a download> + Blob trick.
//  - Capacitor iOS / Android app: WKWebView / Android WebView ignore
//    <a download> (nothing happens, or the CSV opens as plain text with
//    no way back). Instead the file is written to the app's cache folder
//    with @capacitor/filesystem, then handed to the native share sheet
//    with @capacitor/share, where the user can pick "Save to Files",
//    Mail, AirDrop, WhatsApp, Excel, Numbers, etc.
//
// Requires (once):  npm install @capacitor/filesystem @capacitor/share
//                   npx cap sync
//
// Native detection uses the window.Capacitor global the native shell
// injects - same approach as isNativeAppShell() in LiveWanPanel.jsx.

import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

function isNative() {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch {
    return false;
  }
}

function browserDownload(text, filename, mimeType) {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function nativeShare(text, filename, dialogTitle) {
  // Cache directory: no permissions needed on iOS or Android, and the OS
  // clears it on its own. Overwrites a same-named file from a previous
  // export (same device + same day).
  const { uri } = await Filesystem.writeFile({
    path: filename,
    data: text,
    directory: Directory.Cache,
    encoding: Encoding.UTF8,
  });

  try {
    await Share.share({
      title: filename,
      dialogTitle: dialogTitle || 'Export',
      files: [uri],
    });
  } catch (e) {
    // Closing the share sheet without picking anything rejects with
    // "Share canceled" - that's the user's choice, not an error.
    if (/cancel/i.test(String(e?.message || e))) return;
    throw e;
  }
}

/**
 * @param {string} text      file contents
 * @param {string} filename  e.g. "Hana_clients_2026-10-13.csv"
 * @param {object} [opts]
 * @param {string} [opts.mimeType='text/csv;charset=utf-8;']
 * @param {string} [opts.dialogTitle]  Android share-sheet title
 */
export async function saveTextFile(text, filename, opts = {}) {
  const mimeType = opts.mimeType || 'text/csv;charset=utf-8;';
  if (isNative()) {
    await nativeShare(text, filename, opts.dialogTitle);
  } else {
    browserDownload(text, filename, mimeType);
  }
}

// ---------------------------------------------------------------------------
// Binary files (PDF etc.)
// ---------------------------------------------------------------------------

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // result is "data:application/pdf;base64,XXXX" - keep only the XXXX part
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsDataURL(blob);
  });
}

function browserDownloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Save a Blob (e.g. a PDF from fetch().blob()).
 * Browser: normal download. iOS / Android app: cache file + share sheet.
 *
 * @param {Blob}   blob
 * @param {string} filename  e.g. "router-report-daily-ABC123-2026-10-15.pdf"
 * @param {object} [opts]
 * @param {string} [opts.dialogTitle]  Android share-sheet title
 */
export async function saveBlobFile(blob, filename, opts = {}) {
  if (!isNative()) {
    browserDownloadBlob(blob, filename);
    return;
  }

  // No `encoding` => Filesystem treats `data` as base64 and writes raw bytes.
  const { uri } = await Filesystem.writeFile({
    path: filename,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
  });

  try {
    await Share.share({
      title: filename,
      dialogTitle: opts.dialogTitle || 'Save file',
      files: [uri],
    });
  } catch (e) {
    if (/cancel/i.test(String(e?.message || e))) return;
    throw e;
  }
}
